'use client';

import React, { useState, useRef, useTransition } from 'react';
import styles from './OtpModal.module.scss';
import { useTranslation } from '@/hooks/useTranslation';

interface OtpModalProps {
  userId: string;
  email: string;
  onSuccess: () => void;
  onClose: () => void;
}

export default function OtpModal({ userId, email, onSuccess, onClose }: OtpModalProps) {
  const { t } = useTranslation();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [isPending, startTransition] = useTransition();
  const [isResending, startResend] = useTransition();

  const AUTH_ERROR_MAP: Record<string, string> = {
    'auth.errors.invalidOtp': t('auth.errors.invalidOtp'),
    'auth.errors.expiredMessage': t('auth.otp.expiredMessage'),
    'auth.errors.genericError': t('auth.errors.genericError'),
  };
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  const handleDigit = (idx: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[idx] = value;
    setDigits(next);
    if (value && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(''));
      refs.current[5]?.focus();
    }
  };

  const handleVerify = () => {
    const code = digits.join('');
    if (code.length < 6) { setError(t('errors.validation.required')); return; }
    setError('');

    startTransition(async () => {
      try {
        const res = await fetch('/api/v1/auth/verify-otp', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, code }),
        });
        const json = await res.json();
        if (!res.ok) {
          setError(AUTH_ERROR_MAP[json.message] || t('auth.errors.invalidOtp'));
          setDigits(['', '', '', '', '', '']);
          refs.current[0]?.focus();
          return;
        }
        onSuccess();
      } catch {
        setError(t('errors.network.serverError'));
      }
    });
  };

  const handleResend = () => {
    setResendMessage('');
    setError('');

    startResend(async () => {
      try {
        const res = await fetch('/api/v1/auth/verify-otp', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId }),
        });
        const json = await res.json();
        if (!res.ok) {
          setError(AUTH_ERROR_MAP[json.message] || t('auth.errors.genericError'));
          return;
        }
        setResendMessage(t('auth.otp.successMessage'));
      } catch {
        setError(t('errors.network.serverError'));
      }
    });
  };

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="otp-title">
      <div className={styles.modal}>
        <button className={styles.closeBtn} onClick={onClose} aria-label={t('common.close')}>×</button>

        <div className={styles.icon}>✉️</div>
        <h2 id="otp-title" className={styles.title}>{t('auth.otp.title')}</h2>
        <p className={styles.subtitle}>
          {t('auth.otp.subtitle').replace('{{email}}', '')}<br />
          <strong>{email}</strong>
        </p>

        {error && <div className={styles.error} role="alert">{error}</div>}
        {resendMessage && <div className={styles.success}>{resendMessage}</div>}

        <div className={styles.digits} onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              className={`${styles.digit} ${error ? styles.digitError : ''}`}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              id={`otp-digit-${i}`}
              aria-label={`Dígito ${i + 1}`}
            />
          ))}
        </div>

        <button
          id="btn-verify-otp"
          className={styles.verifyBtn}
          onClick={handleVerify}
          disabled={isPending}
        >
          <span className={styles.spinner} aria-hidden="true" data-visible={isPending} />
          {isPending ? t('common.loading') : t('auth.otp.submitButton')}
        </button>

        <p className={styles.resendPrompt}>
          {t('auth.login.noAccount') ? 'Não recebeu o código?' : 'Didn\'t receive the code?'}{' '}
          <button
            className={styles.resendLink}
            onClick={handleResend}
            disabled={isResending}
          >
            {isResending ? t('common.loading') : t('auth.otp.resendButton')}
          </button>
        </p>
      </div>
    </div>
  );
}
